/* eslint-disable @typescript-eslint/no-explicit-any */
import { DATE_MODEL, IMAGE_STORE_BASE_URL, TIME_MODEL } from '../constants'
import { AnswerTypes } from '../components/AnswerRenderer/types'
import moment, { MomentInput } from 'moment'
import { MixedTypeAnswerData, MixedTypeAnswerModel } from '../types'

class Helpers {
    formatDateTime = (date: MomentInput): string => {
        return moment(date).format(`${DATE_MODEL()} ${TIME_MODEL()}`)
    }

    getAnswerType = (answer: string | number | any[] | undefined, resultType: string | undefined): AnswerTypes => {
        switch (resultType) {
            case 'snail':
                return AnswerTypes.Snail
            case 'plot':
                return AnswerTypes.Image
            case 'plotly':
                return AnswerTypes.Chart
            case 'dataframe':
                return AnswerTypes.Dataframe
            case 'mixed':
                return AnswerTypes.Mixed
            case 'string':
                return AnswerTypes.String
        }

        if (typeof answer === 'string' && answer.startsWith('[') && answer.endsWith(']')) {
            return AnswerTypes.StringifiedArray
        }

        if (Array.isArray(answer)) {
            return AnswerTypes.Array
        }

        return AnswerTypes.String
    }

    parseSuggestions = (suggestions: string | string[] | undefined): string[] => {
        if (!suggestions) return []
        if (Array.isArray(suggestions)) {
            return suggestions.filter((item): item is string => typeof item === 'string')
        }
        if (typeof suggestions === 'string') {
            try {
                const parsed = JSON.parse(suggestions)
                return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : []
            } catch {
                const trimmed = suggestions.trim()
                if (!trimmed || trimmed.startsWith('[')) return []
                return [suggestions]
            }
        }
        return []
    }

    normalizeMixedAnswerItem = (item?: MixedTypeAnswerData): MixedTypeAnswerData | undefined => {
        if (!item) return undefined

        if (typeof item.value === 'object' && item.value !== null && !Array.isArray(item.value) && (item.value as any).result !== undefined) {
            return {
                ...item,
                type: AnswerTypes.String,
                value: String((item.value as any).result),
            }
        }

        return item
    }

    private isProcessedMixedItem = (item: unknown): item is MixedTypeAnswerModel => {
        return !!item && typeof item === 'object' && !Array.isArray(item) && typeof (item as MixedTypeAnswerModel).description === 'string'
    }

    normalizeMixedAnswers = (answer: unknown): MixedTypeAnswerModel[] | null => {
        if (Array.isArray(answer) && answer.length > 0 && answer.every(this.isProcessedMixedItem)) {
            return answer.map((item) => ({
                description: item.description,
                data: this.normalizeMixedAnswerItem(item.data),
            }))
        }

        let parsed: any = answer
        if (typeof answer === 'string') {
            if (!answer.trim()) {
                return null
            }
            try {
                parsed = JSON.parse(answer)
            } catch {
                return null
            }
        }

        const value = Array.isArray(parsed) ? parsed : parsed?.value
        if (!Array.isArray(value)) {
            return null
        }

        return value.map((item) => ({
            description: '',
            data: this.normalizeMixedAnswerItem(item),
        }))
    }

    hasDisplayableAnswer = (answer: unknown): boolean => {
        if (answer === undefined || answer === null || answer === '') {
            return false
        }

        if (Array.isArray(answer)) {
            return true
        }

        if (typeof answer === 'object') {
            return Object.keys(answer as object).length > 0
        }

        return true
    }

    toDisplayText = (resultAnswer: unknown): string | undefined => {
        if (resultAnswer === null || resultAnswer === undefined || resultAnswer === '') {
            return undefined
        }

        if (typeof resultAnswer === 'number' || typeof resultAnswer === 'boolean') {
            return String(resultAnswer)
        }

        if (typeof resultAnswer === 'object') {
            const result = (resultAnswer as { result?: unknown }).result
            if (result !== undefined && result !== null && result !== '') {
                return String(result)
            }
            return undefined
        }

        if (typeof resultAnswer !== 'string') {
            return String(resultAnswer)
        }

        try {
            const parsed = JSON.parse(resultAnswer)
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && parsed.result !== undefined && parsed.result !== null) {
                return String(parsed.result)
            }
            if (typeof parsed === 'string' || typeof parsed === 'number' || typeof parsed === 'boolean') {
                return String(parsed)
            }
            return undefined
        } catch {
            return resultAnswer
        }
    }

    sanitizeMarkdownSource = (markdown: string): string => {
        return markdown
            .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
            .replace(/<[^>]+>/g, '')
            .replace(/\]\(\s*(?:javascript|vbscript|data):[^)]*\)/gi, ']()')
            .replace(/\b(?:javascript|vbscript|data):/gi, '')
    }

    buildImageUrl = (resultAnswer: unknown, baseUrl = IMAGE_STORE_BASE_URL): string => {
        const fileName = encodeURIComponent(String(resultAnswer ?? '').replace(/^\/+/, ''))
        return `${baseUrl.replace(/\/$/, '')}/${fileName}`
    }
}

export const helpers = new Helpers()
